import React, { useEffect, useMemo, useState } from 'react';
import { Monitor, PlayCircle } from 'lucide-react';
import { toast } from 'sonner';

import DataTable from '../components/DataTable';
import EmptyState from '../components/EmptyState';
import LoadingSkeleton from '../components/LoadingSkeleton';
import StatusBadge from '../components/StatusBadge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { devicesApi, remoteApi } from '../lib/api';

function formatTimestamp(value) {
  if (!value) return '—';
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
}

function sessionDuration(session) {
  if (!session.started_at) return '—';
  const end = session.stopped_at ? new Date(session.stopped_at) : new Date();
  const seconds = Math.max(0, Math.round((end - new Date(session.started_at)) / 1000));
  const mins = Math.floor(seconds / 60);
  const hrs = Math.floor(mins / 60);
  if (hrs) return `${hrs}h ${mins % 60}m`;
  return `${mins}m`;
}

export default function RemoteSessions() {
  const [sessions, setSessions] = useState([]);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showStart, setShowStart] = useState(false);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = 12;
  const [form, setForm] = useState({ device_id: '', reason: '', mode: 'control' });

  const loadRemoteSessions = async () => {
    setLoading(true);
    setError('');
    try {
      const [sessionsResponse, devicesResponse] = await Promise.all([
        remoteApi.listSessions({ page, page_size: pageSize }),
        devicesApi.list({ page: 1, page_size: 200 }),
      ]);
      setSessions(sessionsResponse.data?.items || []);
      setTotal(sessionsResponse.data?.total || 0);
      setDevices(devicesResponse.data?.items || []);
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to load remote sessions.';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRemoteSessions();
  }, [page]);

  const startSession = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      await remoteApi.startSession(form);
      toast.success('Remote session started.');
      setShowStart(false);
      setForm({ device_id: '', reason: '', mode: 'control' });
      loadRemoteSessions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start session.');
    } finally {
      setSaving(false);
    }
  };

  const stopSession = async (id) => {
    try {
      await remoteApi.stopSession({ session_id: id });
      toast.success('Remote session stopped.');
      loadRemoteSessions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to stop session.');
    }
  };

  const availableDevices = useMemo(
    () => devices.filter((device) => device.status === 'online' && device.remote_access_ready),
    [devices]
  );

  return (
    <div className="min-h-screen space-y-6 bg-slate-900 p-6 text-slate-300" data-testid="remote-sessions-page">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Remote Sessions</h1>
          <p className="mt-2 text-slate-500">
            Start audited remote access, review active sessions, and stop connections when work is complete.
          </p>
        </div>
        <Button className="bg-blue-600 text-white hover:bg-blue-700" onClick={() => setShowStart(true)}>
          <PlayCircle className="h-4 w-4" />
          Start Session
        </Button>
      </div>

      <Card className="border-slate-700 bg-slate-800 shadow-lg">
        <CardHeader className="pb-0">
          <CardTitle className="text-white">Session History</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 pt-6">
          {error ? (
            <div className="rounded-lg border border-red-500/20 bg-red-500/10 p-4 text-sm text-red-300">
              {error}
            </div>
          ) : null}

          {loading ? (
            <LoadingSkeleton type="table" />
          ) : sessions.length ? (
            <DataTable
              columns={[
                { key: 'device_hostname', label: 'Device' },
                {
                  key: 'requester_email',
                  label: 'Operator',
                  render: (value) => value || 'Unknown',
                },
                {
                  key: 'started_at',
                  label: 'Started At',
                  render: (value) => formatTimestamp(value),
                },
                {
                  key: 'duration',
                  label: 'Duration',
                  render: (_, row) => sessionDuration(row),
                },
                {
                  key: 'status',
                  label: 'Status',
                  render: (value) => <StatusBadge status={value || 'idle'} />,
                },
                {
                  key: 'actions',
                  label: 'Actions',
                  render: (_, row) =>
                    row.status === 'started' ? (
                      <Button
                        size="sm"
                        className="bg-red-600 text-white hover:bg-red-700"
                        onClick={(event) => {
                          event.stopPropagation();
                          stopSession(row.id);
                        }}
                      >
                        Stop
                      </Button>
                    ) : (
                      <span className="text-slate-500">—</span>
                    ),
                },
              ]}
              data={sessions}
              loading={loading}
              emptyMessage="No remote sessions are recorded yet."
              pagination={{
                page,
                pageSize,
                total,
                onPageChange: setPage,
              }}
            />
          ) : (
            <EmptyState
              icon={<Monitor className="h-8 w-8" />}
              title="No remote sessions"
              description="Start a new remote session to populate this history."
            />
          )}
        </CardContent>
      </Card>

      <Dialog open={showStart} onOpenChange={setShowStart}>
        <DialogContent className="border-slate-700 bg-slate-800 text-slate-300">
          <DialogHeader>
            <DialogTitle className="text-white">Start Session</DialogTitle>
          </DialogHeader>
          <form className="space-y-4" onSubmit={startSession}>
            <select
              value={form.device_id}
              onChange={(event) => setForm((current) => ({ ...current, device_id: event.target.value }))}
              className="w-full rounded-lg border border-slate-600 bg-slate-700 px-4 py-3 text-white outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="">Select device</option>
              {availableDevices.map((device) => (
                <option key={device.id} value={device.id}>
                  {device.hostname} ({device.employee_name || 'Unassigned'})
                </option>
              ))}
            </select>
            <textarea
              value={form.reason}
              onChange={(event) => setForm((current) => ({ ...current, reason: event.target.value }))}
              placeholder="Reason for access"
              className="min-h-[120px] w-full rounded-lg border border-slate-600 bg-slate-700 px-4 py-3 text-white placeholder:text-slate-500 outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
            <Button className="w-full bg-blue-600 text-white hover:bg-blue-700" disabled={saving} type="submit">
              {saving ? 'Starting...' : 'Start Session'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
